import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { CardData as CardDataType, CardDataFormSubmit } from '../pages/UserPages/wallet-card/type'
import { useCreateWalletCardDraftMutation, useCreateWalletCardMutation as useCreateWalletCardRTK, useLazyGetWalletCardQuery, useUpdateWalletCardDraftMutation, useUpdateWalletCardMutation } from '../../services/cellcast.v2'
import { useI18n } from '../Utils/_helpers/useI18n';

// Use the CardData type from the wallet-card module
export type CardData = CardDataType;

/**
 * Custom hook for wallet card operations, wrapping RTK Query
 */
export const useWalletCard = () => {
  const { t } = useI18n();
  // Use the RTK Query mutation
  const [createWalletCardRTK, { isLoading }] = useCreateWalletCardRTK();
  const [updateWalletCardRTK, { isLoading: isUpdating }] = useUpdateWalletCardMutation();
  const [createWalletCardDraftRTK, { isLoading: isDraftCreating }] = useCreateWalletCardDraftMutation();
  const [updateWalletCardDraftRTK, { isLoading: isDraftUpdating }] = useUpdateWalletCardDraftMutation();
  const [getWalletCard] = useLazyGetWalletCardQuery();

  // Create a new wallet card
  const createWalletCard = async (cardData: CardDataFormSubmit) => {
    try {
      const result = await createWalletCardRTK(cardData).unwrap();
      toast.success('Wallet card created successfully');
      if (result.data?.id) {
        return result.data.id;
      } else {
        throw new Error(t('IMAGE.CROP.ERROR', { name: 'create' }));
      }
    } catch (error: any) {
      toast.error(error.data?.message || t('IMAGE.CROP.ERROR', { name: 'create' }));
      throw error;
    }
  }

  // Update wallet card
  const updateWalletCard = async (cardData: CardDataFormSubmit) => {
    try {
      const result = await updateWalletCardRTK(cardData).unwrap();
      toast.success('Wallet card updated successfully');
      if (result.data?.id) {
        return result.data.id;
      } else {
        throw new Error(t('IMAGE.CROP.ERROR', { name: 'update' }));
      }
    } catch (error: any) {
      toast.error(error.data?.message || t('IMAGE.CROP.ERROR', { name: 'update' }));
      throw error;
    }
  }
  // Get wallet card by ID
  const getWalletCardById = async (id: string) => {
    if (!id) return null;
    
    try {
      const result = await getWalletCard({ id }).unwrap();
      return result;
    } catch (error: any) {
      toast.error(error.data?.message || t('IMAGE.CROP.ERROR', { name: 'fetch' }));
      throw error;
    }
  }

  // Save wallet card as draft
  const saveWalletCardDraft = async (cardData: CardDataFormSubmit) => {
    try {
      const result = await createWalletCardDraftRTK(cardData).unwrap();
      toast.success('Wallet card draft saved successfully');
      if (result.data?.id) {
        return result.data.id;
      } else {
        throw new Error(t('IMAGE.CROP.ERROR', { name: 'save draft' }));
      }
    } catch (error: any) {
      toast.error(error.data?.message || t('IMAGE.CROP.ERROR', { name: 'save draft' }));
      throw error;
    }
  }

  // Update wallet card draft
  const updateWalletCardDraft = async (cardData: CardDataFormSubmit) => {
    try {
      const result = await updateWalletCardDraftRTK(cardData).unwrap();
      toast.success('Wallet card draft updated successfully');
      if (result.data?.id) {
        return result.data.id;
      } else {
        throw new Error(t('IMAGE.CROP.ERROR', { name: 'update draft' }));
      }
    } catch (error: any) {
      toast.error(error.data?.message || t('IMAGE.CROP.ERROR', { name: 'update draft' }));
      throw error;
    }
  }

  return {
    createWalletCard,
    updateWalletCard,
    getWalletCardById,
    isLoading,
    saveWalletCardDraft,
    updateWalletCardDraft,
    isDraftCreating,
    isDraftUpdating,
  };
}

export default useWalletCard;
